import {CustomerFilterOperators} from "../components/common/CustomerFilter";
import {QueryStringFilter} from "../generated/graphql";
import {AG_GRID_NOT_EXIST_FILTER} from "./ag-grid/AGGridUtils";

export const makeQueryStringFilter = (query: string, fields: string[]) => {
  const escapedQuery = query.includes(" ") ? `"${query}"` : `*${query}*`;
  return {
    query: escapedQuery,
    fields: fields
  } as QueryStringFilter;
};

/**
 * Builds a dispatch-group filter fragment for a search query.
 * - Non-empty ids -> OR of equality matches on `field`.
 * - Empty ids     -> match-none clause `{ [field]: { eq: -1 } }`. -1 is never a real
 *   dispatchGroupId, so a selected station with no groups returns zero rows (mirrors
 *   the empty-setNumber idiom in createCondition below).
 */
export const buildDispatchGroupFilter = (field: string, ids: Array<number | string>): Record<string, unknown> => {
  if (ids.length === 0) {
    return {[field]: {eq: -1}};
  }
  return {or: ids.map((id) => ({[field]: {eq: id}}))};
};

export const buildQuery = (cols: string[]) => {
  const set = (o: {[key: string]: any} = {}, a: string[]) => {
    const k = a.shift() as string;
    o[k] = a.length ? set(o[k], a) : null;
    return o;
  };

  const o = cols.reduce((o: object, a: string) => set(o, a.split(".")), {});

  return JSON.stringify(o)
    .replace(/"|:|null/g, "")
    .replace(/^\{/, "")
    .replace(/}$/, "");
};

export const createCondition = (
  column: string,
  filter: any,
  columnKey: string,
  customOperatorFields: Map<string | undefined, string>
): any => {
  if (filter.operator) {
    return {
      [filter.operator.toLowerCase()]: [
        createCondition(column, filter.condition1, columnKey, customOperatorFields),
        createCondition(column, filter.condition2, columnKey, customOperatorFields)
      ]
    };
  }
  if (filter.filterType === "set") {
    if (filter.values.length === 0) {
      return {[column]: {match: ""}};
    }
    return {
      or: filter.values.map((x: string | null) => {
        if (x === AG_GRID_NOT_EXIST_FILTER) {
          return {[column]: {exists: false}};
        }
        return {[column]: {matchPhrase: x}};
      })
    };
  }
  if (filter.filterType === "setNumber") {
    if (filter.values.length === 0) {
      return {
        [column]: {eq: -1}
      };
    }
    if (filter?.customerFilterOperator === CustomerFilterOperators.NotEqual) {
      return {
        and: filter.values.map(Number).map((x: number) => {
          return {[column]: {ne: x}};
        })
      };
    }
    return {
      or: filter.values.map((x: string) => {
        if (x === AG_GRID_NOT_EXIST_FILTER) {
          return {[column]: {exists: false}};
        }
        return {[column]: {eq: Number(x)}};
      })
    };
  }
  const customTextOperator = customOperatorFields.has(columnKey) ? customOperatorFields.get(columnKey)! : "wildcard";

  switch (filter.type) {
    case "equals":
      if (filter.filterType === "number") {
        return {[column]: {eq: filter.filter}};
      }
      return {[column]: {matchPhrase: filter.filter}};
    case "notEqual":
      return {[column]: {ne: filter.filter}};
    case "lessThan":
      return {[column]: {lt: filter.filter}};
    case "lessThanOrEqual":
      return {[column]: {lte: filter.filter}};
    case "greaterThan":
      return {[column]: {gt: filter.filter}};
    case "greaterThanOrEqual":
      return {[column]: {gte: filter.filter}};
    case "inRange":
      return {[column]: {range: [filter.filter, filter.filterTo]}};
    case "contains":
      return {[column]: {[customTextOperator]: `*${filter.filter}*`}};
    case "notContains":
      return {not: {[column]: {[customTextOperator]: `*${filter.filter}*`}}};
    case "startsWith":
      return {[column]: {wildcard: `${filter.filter}*`}};
    case "endsWith":
      return {[column]: {wildcard: `*${filter.filter}`}};
    case "blank":
      return {[column]: {match: filter.filterType === "number" ? null : ""}};
    case "notBlank":
      return {not: {[column]: {match: filter.filterType === "number" ? null : ""}}};
  }
};
